import React from 'react';
import { SortableList } from '../../../components/SortableList/SortableList';
import { Card } from '../../../components/Card/Card';
import { MerchProductVariant } from '../../../model/product';
import { StatusPill } from '../../../components/StatusPill/StatusPill';
import { sortBy } from 'lodash';
import './ProductDetails.css';
import { t } from '../../../locales/translate';
import { Link } from 'react-router-dom';
import { ordersPath } from '../../../routes';
import { deleteSuffix } from '../../../util/util';
import { isAdmin, useSelectedMembership } from '../../../model/membership';

interface Props {
  variants?: MerchProductVariant[];
}

function OrderPerItemsStatusLink({
  property,
  row,
  link = true,
}: {
  property: keyof MerchProductVariant;
  row: MerchProductVariant;
  link?: boolean;
}): JSX.Element {
  const itemStatus = deleteSuffix(property, 'Count');
  return link ? (
    <Link
      to={ordersPath({
        productVariantId: row.sku,
        itemStatus,
      })}
    >
      {row[property]}
    </Link>
  ) : (
    <span>{row[property]}</span>
  );
}

export function Stocks({ variants }: Props): JSX.Element | null {
  const membership = useSelectedMembership();

  if (!variants) {
    return null;
  }
  const linkToOrderSearch = isAdmin(membership);
  return (
    <Card title={t.model.product.stock} fullbleed>
      <SortableList
        items={sortBy(variants, ['order'])}
        columns={[
          {
            width: 150,
            label: t.model.productVariant.sku,
            sortable: true,
            dataKey: 'sku',
          },
          {
            width: 150,
            label: t.model.variant._name,
            sortable: true,
            dataKey: 'description',
          },
          {
            width: 150,
            label: t.model.product.minAvailability,
            sortable: true,
            dataKey: 'minimumStockCount',
            cellRenderer: (row) => row.minimumStockCount || 0,
          },
          {
            width: 150,
            label: t.model.product.stock,
            sortable: true,
            dataKey: 'stockCount',
          },
          {
            width: 150,
            label: t.model.status.available,
            sortable: true,
            dataKey: 'availableCount',
            cellRenderer: (row) => (
              <LabelWithSmallCircle
                text={`${row.availableCount}`}
                color={
                  (row.availableCount || 0) >= (row.minimumStockCount || 0)
                    ? 'var(--color-success)'
                    : 'var(--color-error)'
                }
              />
            ),
          },
          {
            width: 150,
            label: t.model.status.reserved,
            sortable: true,
            dataKey: 'reservedCount',
            cellRenderer: (row) => (
              <div>
                <OrderPerItemsStatusLink property="reservedCount" row={row} link={linkToOrderSearch} />
              </div>
            ),
          },
          {
            width: 150,
            label: t.model.status.cart,
            sortable: true,
            dataKey: 'cartCount',
            cellRenderer: (row) => (
              <div>
                <OrderPerItemsStatusLink property="cartCount" row={row} link={linkToOrderSearch} />
              </div>
            ),
          },
          {
            width: 150,
            label: t.model.status.fulfillment,
            sortable: true,
            dataKey: 'fulfillmentCount',
            cellRenderer: (row) => (
              <div>
                <OrderPerItemsStatusLink property="pendingDeliveryCount" row={row} link={linkToOrderSearch} /> /{' '}
                <OrderPerItemsStatusLink property="fulfillmentCount" row={row} link={linkToOrderSearch} />
              </div>
            ),
          },
          {
            width: 150,
            label: t.model.status.incomplete,
            sortable: true,
            dataKey: 'incompleteCount',
            cellRenderer: (row) => (
              <div>
                <OrderPerItemsStatusLink property="incompleteCount" row={row} link={linkToOrderSearch} />
              </div>
            ),
          },
          {
            width: 150,
            label: t.model.status._name,
            sortable: true,
            dataKey: 'active',
            cellRenderer: (row) => <StatusPill status={row.active ? 'active' : 'inactive'} />,
          },
        ]}
      />
    </Card>
  );
}

function LabelWithSmallCircle({ text, color }: { text: string; color: string }): JSX.Element {
  return (
    <div className="stock__stock-value">
      {text}
      <svg height="20" width="20">
        <circle cx="10" cy="10" r="5" fill={color} strokeWidth="7" />
      </svg>
    </div>
  );
}
